import {
  Autocomplete,
  Backdrop,
  useControlled,
  AutocompleteProps,
} from '@mui/material';
import { AutocompleteOwnerState } from '@mui/material/Autocomplete';
import { AutocompleteCloseReason } from '@mui/material/useAutocomplete';
import { SyntheticEvent } from 'react';
import { useScrollLock } from 'usehooks-ts';
import { MAP_FORM_LAYOUT_SCROLL_LOCK_SELECTOR } from 'components/MapFormLayout';

type AutocompleteWithBackdropExtraProps = {
  /**
   * Scroll container to lock while the list is open (in addition to
   * `document.body`). A string is passed to `document.querySelector` (first
   * match). Defaults to `MAP_FORM_LAYOUT_SCROLL_LOCK_SELECTOR` from
   * `MapFormLayout`, which marks a single active scroll root per breakpoint.
   */
  scrollLockTarget?: HTMLElement | string | null;
};

const getScrollLockElement = (
  target: HTMLElement | string | null | undefined,
): HTMLElement | null => {
  if (!target) {
    return null;
  }
  if (typeof target === 'string') {
    return document.querySelector<HTMLElement>(target);
  }
  return target;
};

/**
 * Autocomplete with an invisible full-page `Backdrop` while open (blocks
 * pointer events behind the field). List paper uses Popover/Menu-style
 * elevation (`elevation={8}`) for the same drop shadow as MUI
 * `Menu`/`Popover`. Backdrop click closes (reason `'blur'`).
 *
 * If MUI ships Autocomplete on Base UI with better modal/backdrop behavior,
 * this wrapper may become unnecessary; see
 * https://github.com/mui/material-ui/issues/21661 and
 * https://github.com/mui/material-ui/issues/27686.
 */
export const AutocompleteWithBackdrop = <
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> &
    AutocompleteWithBackdropExtraProps,
): JSX.Element => {
  const {
    open: openProp,
    onOpen,
    onClose,
    slotProps,
    scrollLockTarget = MAP_FORM_LAYOUT_SCROLL_LOCK_SELECTOR,
    ...rest
  } = props;

  const [open, setOpen] = useControlled({
    controlled: openProp,
    default: false,
    name: 'AutocompleteWithBackdrop',
    state: 'open',
  });

  const scrollLockElement = open
    ? getScrollLockElement(scrollLockTarget)
    : null;

  useScrollLock({ autoLock: open }); // Scroll lock for body
  useScrollLock({
    autoLock: open && Boolean(scrollLockElement),
    lockTarget: scrollLockElement ?? undefined,
  });

  const handleOpen = (event: SyntheticEvent): void => {
    setOpen(true);
    onOpen?.(event);
  };

  const handleClose = (
    event: SyntheticEvent,
    reason: AutocompleteCloseReason,
  ): void => {
    setOpen(false);
    onClose?.(event, reason);
  };

  const paperSlot = slotProps?.paper;
  const mergedSlotProps = {
    ...slotProps,
    paper:
      typeof paperSlot === 'function'
        ? (
            ownerState: AutocompleteOwnerState<
              Value,
              Multiple,
              DisableClearable,
              FreeSolo
            >,
          ) => ({ elevation: 8, ...paperSlot(ownerState) })
        : { elevation: 8, ...paperSlot },
  };

  return (
    <>
      <Backdrop
        invisible
        open={open}
        onMouseDown={(event) => {
          event.preventDefault();
          handleClose(event, 'blur');
        }}
        // Otherwise wheel/touch chain-scrolls nested `overflow: auto` (e.g. MapFormLayout form).
        onWheel={(event) => {
          event.preventDefault();
        }}
        onTouchMove={(event) => {
          event.preventDefault();
        }}
        sx={(theme) => ({
          // MUI Autocomplete's Popper renders at zIndex.modal (1300), so the
          // Backdrop sits just below it at modal-1 (1299). The input field has
          // no explicit z-index and is covered by the Backdrop while open —
          // keyboard input still works since focus is already on the input.
          zIndex: theme.zIndex.modal - 1,
          overscrollBehavior: 'none',
          touchAction: 'none',
        })}
      />
      <Autocomplete
        {...rest}
        slotProps={mergedSlotProps}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
      />
    </>
  );
};
